import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {CheckboxTheme} from './types';
import {template} from './template';
import css from './index.styl?inline';

export interface CheckboxJsxProps {
  attrs?: {
    checked?: boolean;
    'checked-color'?: string;
    theme?: string;
    'checked-icon'?: string;
    disabled?: boolean;
    focused?: boolean;
    icon?: string;
    'unchecked-icon'?: string;
  };
}

export class Checkbox extends Component<EmptyObj, CheckboxJsxProps['attrs']> {
  static get attrsSchema(): AttrsSchema<CheckboxJsxProps['attrs']> {
    return {
      ...super.attrsSchema,
      checked: `boolean`,
      'checked-color': {
        type: `string`,
        description: `custom checked color that overrides theme attribute, does not apply css states.`,
      },
      theme: {
        type: `string`,
        default: CheckboxTheme.Blue,
        description: `checkbox theme with design system colors and css states.`,
        enum: Object.values(CheckboxTheme),
      },
      'checked-icon': {type: `string`, default: `checkmark`},
      disabled: `boolean`,
      focused: `boolean`,
      icon: `string`,
      'unchecked-icon': {type: `string`, default: ``},
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      helpers: {
        getIconAttributes: () => {
          const checked = this.attr(`checked`);
          const color = checked && this.attr(`checked-color`) !== `none` ? `white` : null;

          return {
            icon: this.attr(checked ? `checked-icon` : `unchecked-icon`),
            color,
            empty: !checked && !this.attr(`unchecked-icon`),
          };
        },

        getIconStyles: () => {
          const checkedColor = this.attr(`checked-color`);

          if (!checkedColor) {
            return {};
          }

          const checked = this.attr(`checked`);
          const focused = this.attr(`focused`);

          let border = `none`;

          if ((!checked && !focused) || (checked && checkedColor === `none`)) {
            border = `1px solid #CFD6E4`; // $gray2
          } else {
            border = `1px solid ${checkedColor}`;
          }

          return {
            border,
            ...(checked ? {'background-color': checkedColor} : {}),
          };
        },

        getClasses: () => {
          const {checked, disabled, theme, focused} = this.attrs();

          return {
            'mp-checkbox-checked': !!checked,
            'mp-checkbox-disabled': !!disabled,
            'mp-checkbox-focused': !!focused,
            [`mp-checkbox-${theme}`]: !this.attr(`checked-color`), // if color is provided, override all theming
          };
        },
      },
    };
  }
}

export const CheckboxEl = defineAndMakeJsxFactory<CheckboxJsxProps>(`mp-checkbox`, Checkbox);
